import { BarProps } from 'victory';
import { addAlpha } from '@algoan/deep-dive/theme';
import { theme } from '@algoan/dashboard/theme';
import { BarData } from './use-bank-history-selection';

/**
 * To avoid an overflow of first and last bar in the charts, we must adjust alignment and width of
 * these two bars.
 */
export const getBarAlignmentAndWidth = (
  props: BarProps,
): {
  alignment: NonNullable<BarProps['alignment']>;
  barWidth: number;
} => {
  const range = props.scale.x.range();
  const extent = Math.abs(range[1] - range[0]);
  const maxBarWidth = extent / 100;
  const minBarWidth = 1;
  const barWidth = Math.max(Math.min((0.5 * extent) / (props.data.length + 2), maxBarWidth), minBarWidth);

  switch (props.index) {
    case 0:
      return { alignment: 'start', barWidth: barWidth / 2 };
    case props.data.length - 1:
      return { alignment: 'end', barWidth: barWidth / 2 };
    default:
      return { alignment: 'middle', barWidth };
  }
};

interface GetBarWidthProps {
  rangeWidth: number;
  iteration: number;
  startIndex?: number;
  endIndex?: number;
}

export const getBarWidth = ({ rangeWidth, iteration, startIndex, endIndex }: GetBarWidthProps): number => {
  if (!startIndex || !endIndex) return 1;

  const length = Math.abs(startIndex - endIndex);
  const extent = (rangeWidth / length) * (iteration - 1);
  const maxBarWidth = extent / 100;
  const minBarWidth = 1;

  return Math.max(Math.min((0.5 * extent) / (iteration + 2), maxBarWidth), minBarWidth);
};

interface DataProps {
  originData?: BarData;
  endData?: BarData;
}

export const getRangeEvolution = ({ originData, endData }: DataProps): number => {
  if (!originData || !endData) return 0;
  return originData?.date > endData?.date
    ? originData?.balance - endData?.balance
    : endData?.balance - originData?.balance;
};

export const getTransactionDaysArray = ({ originData, endData }: DataProps): number[] | undefined => {
  if (originData) {
    return endData && originData.date !== endData.date
      ? [originData.date, endData.date].sort((a, b) => a - b)
      : [originData.date];
  }
  return undefined;
};

interface GetBarProps {
  isSelected: boolean;
  hovered: boolean;
}

export const getBarColor = ({ isSelected, hovered }: GetBarProps): string => {
  if (isSelected) return addAlpha(theme.main.deepBlue, 0.12);
  return hovered ? addAlpha(theme.main.deepBlue, 0.05) : '';
};

export const getBarVisibility = ({ isSelected, hovered }: GetBarProps): string => {
  return isSelected || hovered ? 'visible' : 'hidden';
};
